/**
 * Quick-log button model + payload construction.
 *
 * The Today page renders one button per action and, on tap, POSTs the built
 * payload then refetches the affected tiles. Both the button list and the
 * payload builder are pure so the "tap → correct body → refresh" contract is
 * unit-tested without a DOM.
 */

import type { QuicklogInput } from '../api.js';

export type QuickAction =
  | { id: 'coffee'; label: 'Coffee'; hint: string }
  | { id: 'espresso'; label: 'Espresso'; hint: string }
  | { id: 'energy_drink'; label: 'Energy drink'; hint: string }
  | { id: 'water'; label: 'Water'; hint: string }
  | { id: 'weight'; label: 'Weight'; hint: string };

export type QuickActionId = QuickAction['id'];

/** Preset caffeine amounts mirror the server so the hint matches what lands. */
export const QUICK_ACTIONS: readonly QuickAction[] = [
  { id: 'coffee', label: 'Coffee', hint: '95 mg' },
  { id: 'espresso', label: 'Espresso', hint: '63 mg' },
  { id: 'energy_drink', label: 'Energy drink', hint: '160 mg' },
  { id: 'water', label: 'Water', hint: '250 mL' },
  { id: 'weight', label: 'Weight', hint: 'log' },
];

/** Default water pour, in mL, for a one-tap water log. */
export const WATER_ML = 250;

/**
 * The metric types each action affects — used to scope the optimistic refetch
 * so a coffee tap re-reads caffeine, not the whole page.
 */
export const ACTION_AFFECTS: Record<QuickActionId, string[]> = {
  coffee: ['caffeine_mg'],
  espresso: ['caffeine_mg'],
  energy_drink: ['caffeine_mg'],
  water: ['water_ml'],
  weight: ['weight_kg'],
};

/**
 * Build the /api/quicklog body for an action. `weight` requires the numeric
 * value captured from the inline input; the caffeine/water actions are
 * self-contained one-taps.
 */
export function quicklogPayload(id: QuickActionId, value?: number): QuicklogInput {
  switch (id) {
    case 'coffee':
      return { kind: 'caffeine', preset: 'coffee' };
    case 'espresso':
      return { kind: 'caffeine', preset: 'espresso' };
    case 'energy_drink':
      return { kind: 'caffeine', preset: 'energy_drink' };
    case 'water':
      return { kind: 'water', ml: WATER_ML };
    case 'weight': {
      if (value === undefined || !Number.isFinite(value)) {
        throw new Error('weight requires a numeric value');
      }
      return { kind: 'weight', kg: value };
    }
  }
}
